import React from 'react';
import Model from '../common/models/Model';
import { Button } from '../common/Button';
import useI18n from '@/hooks/useI18n';
import {
  useDeactivateBrandUser,
  useReactivateBrandUser,
} from '@/hooks/queries/useUserQueries';
import { toast } from 'react-toastify';

interface DeactivateUserProps {
  isDeactivateModalOpen: boolean;
  setIsDeactivateModalOpen: (arg: boolean) => void;
  userStatus: string;
  userName: string;
  userId: string;
  onSuccess: () => void;
  setIsLoading: (arg: boolean) => void;
}

const DeactivateUserModel: React.FC<DeactivateUserProps> = ({
  isDeactivateModalOpen,
  setIsDeactivateModalOpen,
  userStatus,
  userName,
  userId,
  onSuccess,
  setIsLoading,
}) => {
  const { t } = useI18n();
  const deactivateMutation = useDeactivateBrandUser();
  const reactivateMutation = useReactivateBrandUser();

  const handleDeactivateReactivate = async () => {
    setIsLoading(true);
    setIsDeactivateModalOpen(false);
    try {
      if (userStatus === 'active') {
        await deactivateMutation.mutateAsync(userId);
        toast.success(t('userManagement.userDeactivatedSuccess'));
      } else {
        await reactivateMutation.mutateAsync(userId);
        toast.success(t('userManagement.userReactivatedSuccess'));
      }
      onSuccess();
    } catch (error) {
      if (userStatus === 'active')
        toast.error(t('errors.failedToDeactivateBrandUser'));
      else toast.error(t('errors.failedToReactivateBrandUser'));
    }
  };

  return (
    <Model
      isOpen={isDeactivateModalOpen}
      onClose={() => setIsDeactivateModalOpen(false)}
      className="backdrop-brightness-75"
    >
      <div className="w-[360px] w-max-[560px] border-1 rounded-2xl">
        <h1 className="text-[24px] font-header text-[#212121]">
          {userStatus === 'active'
            ? t('userManagement.actions.deactivateUser')
            : t('userManagement.actions.reactivateUser')}
        </h1>
        <p className="my-4 text-gray-600">
          {userStatus === 'active'
            ? t('userManagement.actionsConfirmationMessage.deactivateUser')
            : t('userManagement.actionsConfirmationMessage.reactivateUser')}
          &nbsp;{userName}?
        </p>
        <div className="mt-6 flex w-full items-center justify-between gap-4">
          <Button
            onClick={() => setIsDeactivateModalOpen(false)}
            variant="outlined"
            className="flex-1 max-w-[220px] h-[44px]"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeactivateReactivate}
            className="flex-1 max-w-[220px]"
          >
            {userStatus === 'active'
              ? t('userManagement.actions.deactivate')
              : t('userManagement.actions.reactivate')}
          </Button>
        </div>
      </div>
    </Model>
  );
};

export default DeactivateUserModel;
